import { useCallback, useEffect, useMemo, useState } from "react";
import { getAutoModSettings, getGuildWelcomeSettings, getSettings } from "../lib/api";
import { SystemDisabledBanner } from "./SystemDisabledBanner";

const resolveFromPayload = (systemKey, payload, { strict = false } = {}) => {
  const source = payload && typeof payload === "object" ? payload : {};

  if (systemKey === "level") {
    if (strict && !Object.prototype.hasOwnProperty.call(source, "xp_system_enabled")) return null;
    return Boolean(source?.xp_system_enabled ?? true);
  }

  if (systemKey === "logs") {
    const logs = source?.logs;
    if (strict && (typeof logs !== "object" || logs === null || !Object.prototype.hasOwnProperty.call(logs, "enabled"))) {
      return null;
    }
    return Boolean(logs?.enabled ?? false);
  }

  if (systemKey === "welcome") {
    const hasWelcomeSignal =
      Object.prototype.hasOwnProperty.call(source, "welcomeEnabled")
      || Object.prototype.hasOwnProperty.call(source, "welcomeImageEnabled")
      || Object.prototype.hasOwnProperty.call(source, "leaveEnabled");
    if (strict && !hasWelcomeSignal) return null;
    return Boolean(source?.welcomeEnabled || source?.welcomeImageEnabled || source?.leaveEnabled);
  }

  if (systemKey === "automod") {
    if (strict && !Object.prototype.hasOwnProperty.call(source, "enabled")) return null;
    return Boolean(source?.enabled);
  }

  return true;
};

const SYSTEM_LABELS = {
  level: { ar: "نظام المستويات", en: "Level System" },
  logs: { ar: "نظام السجلات", en: "Logs System" },
  welcome: { ar: "نظام الترحيب", en: "Welcome System" },
  automod: { ar: "أنظمة الرقابة", en: "Moderation Systems" },
};

export const SystemSubpageGuard = ({ systemKey, children }) => {
  const [guildId, setGuildId] = useState(() => String(localStorage.getItem("tns:selected_guild_id") || ""));
  const [enabled, setEnabled] = useState(null);

  const labels = useMemo(() => SYSTEM_LABELS[systemKey] || SYSTEM_LABELS.level, [systemKey]);

  const loadStatus = useCallback(async () => {
    if (!guildId) {
      setEnabled(null);
      return;
    }

    try {
      if (systemKey === "level" || systemKey === "logs") {
        const settings = await getSettings();
        setEnabled(resolveFromPayload(systemKey, settings || {}));
        return;
      }

      if (systemKey === "welcome") {
        const settings = await getGuildWelcomeSettings(guildId);
        setEnabled(resolveFromPayload(systemKey, settings || {}));
        return;
      }

      if (systemKey === "automod") {
        const settings = await getAutoModSettings();
        setEnabled(resolveFromPayload(systemKey, settings || {}));
        return;
      }

      setEnabled(true);
    } catch {
      setEnabled(true);
    }
  }, [guildId, systemKey]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const handleGuildSelected = (event) => {
      const gid = String(event?.detail?.guild?.id || "");
      setGuildId(gid);
    };

    const handleSettingsUpdated = (event) => {
      if (systemKey !== "level" && systemKey !== "logs") return;
      const next = event?.detail || {};
      if (next?.guild_id) setGuildId(String(next.guild_id));
      const resolved = resolveFromPayload(systemKey, next, { strict: true });
      if (resolved === null) {
        loadStatus();
        return;
      }
      setEnabled(resolved);
    };

    const handleWelcomeUpdated = (event) => {
      if (systemKey !== "welcome") return;
      const details = event?.detail || {};
      const eventGuildId = String(details?.guildId || "");
      if (eventGuildId && guildId && eventGuildId !== guildId) return;
      if (details?.settings && typeof details.settings === "object") {
        const resolved = resolveFromPayload(systemKey, details.settings, { strict: true });
        if (resolved === null) {
          loadStatus();
          return;
        }
        setEnabled(resolved);
        return;
      }
      loadStatus();
    };

    const handleAutoModUpdated = (event) => {
      if (systemKey !== "automod") return;
      const details = event?.detail || {};
      const resolved = resolveFromPayload(systemKey, details, { strict: true });
      if (resolved === null) {
        loadStatus();
        return;
      }
      setEnabled(resolved);
    };

    window.addEventListener("tns:guild-selected", handleGuildSelected);
    window.addEventListener("tns:settings-updated", handleSettingsUpdated);
    window.addEventListener("tns:welcome-settings-updated", handleWelcomeUpdated);
    window.addEventListener("tns:automod-settings-updated", handleAutoModUpdated);

    return () => {
      window.removeEventListener("tns:guild-selected", handleGuildSelected);
      window.removeEventListener("tns:settings-updated", handleSettingsUpdated);
      window.removeEventListener("tns:welcome-settings-updated", handleWelcomeUpdated);
      window.removeEventListener("tns:automod-settings-updated", handleAutoModUpdated);
    };
  }, [guildId, loadStatus, systemKey]);

  return (
    <>
      {enabled === false ? (
        <SystemDisabledBanner systemLabelAr={labels.ar} systemLabelEn={labels.en} />
      ) : null}
      {children}
    </>
  );
};
